import { useState, useCallback, useRef } from 'react';
import { recurringEntriesApi } from '../services/api/endpoints/recurringEntries';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import type {
  RecurringEntry,
  CreateRecurringEntryPayload,
  UpdateRecurringEntryPayload,
  GenerateRecurringResult,
} from '../types/recurring-entries';

export function useRecurringEntries() {
  const [entries, setEntries] = useState<RecurringEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);
  const { currentFiscalYear } = useFiscalYearStore();
  const { effectiveFilter } = useBusinessUnitStore();
  const fiscalYearId = currentFiscalYear?.id;
  const buId = effectiveFilter.businessUnitId;
  const buType = effectiveFilter.businessUnitType;

  const loadEntries = useCallback(async (params?: { isActive?: boolean; frequency?: string }) => {
    const requestId = ++loadRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      // Filtre exercice fiscal + unité commerciale : sans ça, la page
      // affichait TOUTES les récurrences de TOUS les exercices et de
      // toutes les unités, et la génération produisait des écritures
      // potentiellement hors exercice courant (= bug comptabilité).
      const response = await recurringEntriesApi.getAll({
        ...params,
        fiscalYearId,
        businessUnitId: buId,
        businessUnitType: buType,
      } as any);

      if (requestId !== loadRequestIdRef.current) return;

      if (response && 'data' in response) {
        const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
        setEntries(data);
      }
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err.message || 'Erreur lors du chargement des écritures récurrentes');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [fiscalYearId, buId, buType]);

  const createEntry = useCallback(async (data: CreateRecurringEntryPayload): Promise<RecurringEntry | null> => {
    try {
      setError(null);
      // INVARIANT MÉTIER : toute récurrence est rattachée à l'exercice
      // dans lequel elle a été créée. Le scheduler générera ses écritures
      // dans CET exercice (si encore ouvert) — sinon il faudra recréer la
      // récurrence dans le nouvel exercice. Évite les amortissements 2024
      // qui se déclencheraient en 2025.
      const payload = {
        ...data,
        fiscalYearId: (data as any).fiscalYearId ?? fiscalYearId,
        businessUnitId: (data as any).businessUnitId ?? buId,
        businessUnitType: (data as any).businessUnitType ?? buType,
      };
      const response = await recurringEntriesApi.create(payload as any);
      if (response && 'data' in response) {
        const newEntry = (response.data as any)?.data || response.data;
        setEntries(prev => [newEntry as RecurringEntry, ...prev]);
        return newEntry as RecurringEntry;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
      return null;
    }
  }, [fiscalYearId, buId, buType]);

  const updateEntry = useCallback(async (id: string, data: UpdateRecurringEntryPayload): Promise<RecurringEntry | null> => {
    try {
      setError(null);
      const response = await recurringEntriesApi.update(id, data);
      if (response && 'data' in response) {
        const updated = (response.data as any)?.data || response.data;
        setEntries(prev => prev.map(e => e.id === id ? updated as RecurringEntry : e));
        return updated as RecurringEntry;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour');
      return null;
    }
  }, []);

  const deleteEntry = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null);
      await recurringEntriesApi.delete(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  const generateEntries = useCallback(async (): Promise<GenerateRecurringResult | null> => {
    try {
      setError(null);
      // CRITIQUE : passer l'exercice courant à la génération sinon le
      // backend produit les écritures sans contexte FY → elles peuvent
      // tomber dans un exercice clôturé ou dans le mauvais exercice
      // (ex : amortissements de juin créés en juillet quand on a déjà
      // bascule sur l'exercice suivant). Le scheduler côté backend doit
      // refuser si l'exercice n'est pas ouvert.
      const response = await recurringEntriesApi.generate({
        fiscalYearId,
        businessUnitId: buId,
        businessUnitType: buType,
      } as any);
      if (response && 'data' in response) {
        const result = (response.data as any)?.data || response.data;
        // Reload entries to reflect updated nextRunDate
        await loadEntries();
        return result as GenerateRecurringResult;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la génération');
      return null;
    }
  }, [loadEntries, fiscalYearId, buId, buType]);

  // Computed values
  const activeEntries = entries.filter(e => e.isActive);
  const dueEntries = activeEntries.filter(e => new Date(e.nextRunDate) <= new Date());

  return {
    entries,
    loading,
    error,
    loadEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    generateEntries,
    activeEntries,
    dueEntries,
  };
}
